import React, { useState, useEffect } from "react";
import { ProductPhysicalSelectEnum } from "../businessInformation/config";
import {
  Form,
  Input,
  Upload,
  InputNumber,
  Radio,
  Image,
  Alert,
  message,
} from "antd";
import { getProductLogoUploadUrl } from "@/api/createCampaignApi";
import axios from "axios";
import { CloseIcon, InfoIcon, UploadIcon } from "@/components/icons";
import LoadingLottie from "@/components/loading";
import { websiteValidator, linkValidator } from "@/biz/validator";
import "./index.scss";
import { useTranslation } from "react-i18next";

interface Props {
  form: any;
  productInfo: any;
  setProductInfo: Function;
  showPhotoError: boolean;
  setShowPhotoError: Function;
}

const ProductDelivery: React.FC<Props> = ({
  form,
  productInfo,
  setProductInfo,
  showPhotoError,
  setShowPhotoError,
}) => {
  const { t } = useTranslation();
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);
  const getPhotoUpload = async (file: any) => {
    setPhotoUploadLoading(true);
    // 大小大于5MB
    if (file.size > 5 * 1024 * 1024) {
      message.error(t('campaignDetail.otherInformationPreview.fileSizeExceed5MB'));
      setPhotoUploadLoading(false);
      return;
    }

    try {
      const res = await getProductLogoUploadUrl({ key: file.name });
      const presignedUrl = res?.data?.presignedUrl;
      const publicUrl = res?.data?.publicUrl;
      const options = {
        headers: {
          "Content-Type": file.type,
        },
      };
      await axios.put(presignedUrl, file, options);
      setProductInfo({
        ...productInfo,
        physicalProductPhoto: publicUrl,
        physicalProductPhotoName: file?.name,
      });
      setShowPhotoError(false);
      setTimeout(() => {
        return Promise.resolve(presignedUrl);
      }, 0);
    } catch (error: any) {
      message.error(error?.message || t('campaignDetail.otherInformationPreview.uploadProductPhotoFailed'));
    } finally {
      setPhotoUploadLoading(false);
    }
  };

  useEffect(() => {
    setPhysicalProductShipType(
      productInfo?.physicalProductShipType || ProductPhysicalSelectEnum.ship
    );
  }, [productInfo?.physicalProductShipType]);

  const [physicalProductShipType, setPhysicalProductShipType] = useState(
    ProductPhysicalSelectEnum.ship
  );
  const handleRadioChange = (e: any) => {
    setPhysicalProductShipType(e.target.value);
  };

  return (
    <div className="mt-6">
      <div className="self-stretch justify-start text-[#1c2024] text-base font-medium  leading-normal mb-4">
        {t("campaignDetail.otherInformationPreview.productDelivery")}
      </div>
      <div className="product-basic-ship">
        <div className="product-basic-ship-header">
          <Form.Item
            name="physicalProductShipType"
            rules={[{ required: true, message: t('campaignDetail.otherInformationPreview.pleaseSelect') }]}
            className="mb-0"
          >
            <Radio.Group onChange={handleRadioChange}>
              <Radio value={ProductPhysicalSelectEnum.ship}>
                {t("campaignDetail.otherInformationPreview.iWillShipProduct")}
              </Radio>
              <Radio value={ProductPhysicalSelectEnum.video}>
                {t("campaignDetail.otherInformationPreview.noShipmentNeeded")}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </div>
        {physicalProductShipType === ProductPhysicalSelectEnum.ship && (
          <div className="product-basic-ship-content mt-4 border-t border-[#e5e5e5] pt-4">
            <div className="product-basic-ship-content-desc">
              {t("campaignDetail.otherInformationPreview.shipProductDescription")}
            </div>
            <div className="product-basic-ship-content-form">
              <div className="product-ship-from">
                <div className="product-ship-from-item">
                  <div className="product-ship-from-item-label">
                    <span>{t("campaignDetail.otherInformationPreview.productName")}</span>
                    <span className="required">*</span>
                  </div>
                  <Form.Item
                    name="physicalProductName"
                    rules={[
                      {
                        required: true,
                        message: t('campaignDetail.otherInformationPreview.pleaseEnterProductName'),
                      },
                    ]}
                  >
                    <Input maxLength={70} showCount size="large" />
                  </Form.Item>
                </div>
                <div className="product-ship-from-item relative">
                  <div className="product-ship-from-item-label">
                    <span>{t("campaignDetail.otherInformationPreview.productPhoto")}</span>
                    <span className="required">*</span>
                  </div>
                  <div className="product-ship-from-item-photo">
                    <div className="photo-container-left">
                      {productInfo?.physicalProductPhoto ? (
                        <div className="photo-container-left-upload">
                          <Image
                            width={20}
                            src={productInfo?.physicalProductPhoto}
                          />
                          <span>{productInfo?.physicalProductPhotoName}</span>
                        </div>
                      ) : (
                        <Upload
                          listType="picture"
                          accept=".jpg,.jpeg,.png"
                          maxCount={1}
                          showUploadList={false}
                          className="photo-uploader"
                          beforeUpload={getPhotoUpload}
                          rootClassName="photo-container-left-upload"
                          disabled={photoUploadLoading}
                        >
                          <div className="photo-container-left-upload">
                            {photoUploadLoading ? (
                              <LoadingLottie />
                            ) : (
                              <UploadIcon />
                            )}
                            <span>{t('campaignDetail.otherInformationPreview.uploadProductPhotos')}</span>
                          </div>
                        </Upload>
                      )}
                    </div>
                    <div className="photo-container-right">
                      {productInfo?.physicalProductPhoto ? (
                        <span
                          onClick={() => {
                            setProductInfo({
                              ...productInfo,
                              physicalProductPhoto: "",
                              physicalProductPhotoName: "",
                            });
                          }}
                        >
                          <CloseIcon />
                        </span>
                      ) : (
                        <span>{t('campaignDetail.otherInformationPreview.uploadPhotoHint')}</span>
                      )}
                    </div>
                  </div>
                  {showPhotoError && (
                    <div className="text-[#ff4d4f] absolute left-0 bottom-0">
                      {t('campaignDetail.otherInformationPreview.pleaseUploadProductPhotos')}
                    </div>
                  )}
                </div>
                <div className="product-ship-from-item">
                  <div className="product-ship-from-item-label">
                    <span>{t("campaignDetail.otherInformationPreview.productRetailPrice")}</span>
                    <span className="required">*</span>
                  </div>
                  <Form.Item
                    name="physicalProductCashValue"
                    rules={[
                      {
                        required: true,
                        message: t('campaignDetail.otherInformationPreview.pleaseEnterProductCashValue'),
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      addonBefore="$"
                      min={0.01}
                      max={100000}
                      size="large"
                    />
                  </Form.Item>
                </div>
                <div className="inline-flex justify-start items-start gap-1.5">
                  <InfoIcon />
                  <div className="w-[449.14px] justify-start text-[#171717] text-sm font-normal  leading-snug">
                    {t('campaignDetail.otherInformationPreview.platformSubsidyDesc')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDelivery;
